import React, { useEffect, useState } from 'react';
import { SageState } from '../types';

interface WiseOldManProps {
  state: SageState;
}

const WiseOldMan: React.FC<WiseOldManProps> = ({ state }) => {
  const [frame, setFrame] = useState(0);

  // Animation loop
  useEffect(() => {
    const interval = setInterval(() => {
      setFrame((prev) => (prev + 1) % 2);
    }, state === SageState.TALKING ? 150 : 800); // Fast for talking, slow for idle

    return () => clearInterval(interval);
  }, [state]);

  const isMouthOpen = state === SageState.TALKING && frame === 1;
  const isBlinking = state !== SageState.TALKING && frame === 0 && Math.random() > 0.9; // Less frequent blinking

  // 8-bit color palette
  const C_SKIN = '#f0d9b5';
  const C_BEARD = '#e2e8f0';
  const C_ROBE = '#4c1d95'; // Violet 900
  const C_ROBE_L = '#6d28d9'; // Violet 700
  const C_EYE = '#000000';
  const C_HAT_TRIM = '#fbbf24'; // Gold

  return (
    <div className="relative w-40 h-40 mx-auto mb-6 transition-transform hover:scale-105">
      {/* 
        SVG Pixel Art 
        viewBox 0 0 16 16 allows us to draw on a 16x16 grid.
        shape-rendering="crispEdges" ensures it looks like pixel art without anti-aliasing blur.
      */}
      <svg
        viewBox="0 0 16 16"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-full drop-shadow-2xl filter"
        style={{ shapeRendering: 'crispEdges' }}
      >
        {/* Robe/Body */}
        <rect x="2" y="11" width="12" height="5" fill={C_ROBE} />
        <rect x="4" y="11" width="8" height="5" fill={C_ROBE_L} />

        {/* Head/Face */}
        <rect x="5" y="4" width="6" height="7" fill={C_SKIN} />

        {/* Hat - Daoist Sage Style */}
        <rect x="4" y="2" width="8" height="2" fill={C_ROBE} />
        <rect x="6" y="1" width="4" height="1" fill={C_ROBE} />
        <rect x="7" y="0" width="2" height="1" fill={C_HAT_TRIM} />
        <rect x="4" y="3" width="8" height="1" fill={C_ROBE_L} />

        {/* Eyes */}
        {!isBlinking ? (
          <>
            {state === SageState.READING ? (
              // Reading: Eyes looking down
              <>
                <rect x="6" y="7" width="1" height="1" fill={C_EYE} />
                <rect x="9" y="7" width="1" height="1" fill={C_EYE} />
              </>
            ) : (
              // Normal: Eyes looking straight
              <>
                <rect x="6" y="6" width="1" height="1" fill={C_EYE} />
                <rect x="9" y="6" width="1" height="1" fill={C_EYE} />
              </>
            )}
          </>
        ) : (
          <>
            <rect x="6" y="6" width="1" height="1" fill={C_SKIN} opacity="0.8" />
            <rect x="9" y="6" width="1" height="1" fill={C_SKIN} opacity="0.8" />
            <rect x="6" y="6.5" width="1" height="0.5" fill={C_EYE} />
            <rect x="9" y="6.5" width="1" height="0.5" fill={C_EYE} />
          </>
        )}

        {/* Beard (White) - Long flowing beard */}
        <rect x="5" y="8" width="6" height="2" fill={C_BEARD} />
        <rect x="6" y="10" width="4" height="2" fill={C_BEARD} />
        <rect x="7" y="12" width="2" height="2" fill={C_BEARD} />

        {/* Mouth (Hidden behind beard mostly, but visible when talking) */}
        {isMouthOpen && (
          <rect x="7" y="9" width="2" height="1" fill="#7f1d1d" />
        )}

        {/* Hands in sleeves (Simple indication) */}
        <rect x="2" y="12" width="2" height="3" fill={C_ROBE_L} />
        <rect x="12" y="12" width="2" height="3" fill={C_ROBE_L} />
      </svg>

      {/* Status Indicator */}
      <div className="absolute top-0 right-0">
        {state === SageState.THINKING && (
          <div className="font-pixel text-xs bg-white text-black p-2 rounded-lg animate-bounce shadow-lg border-2 border-slate-800">
            ...
          </div>
        )}
      </div>
    </div>
  );
};

export default WiseOldMan;